import { getErrorMessage } from '~/utils'

export const useTeamMembers = defineStore('teamMembers', () => {
  const { $api } = useNuxtApp()
  const user = useUser()

  const data = ref([])
  const meta = ref({})
  const loading = ref(false)
  const error = ref(null)

  const teams = useTeams()

  // Client-side only initialization (only if no SSR data)
  onMounted(() => {
    if (teams.current?.id && data.value.length === 0 && !meta.value.current_page) {
      get()
    }
  })

  // Watch for team changes and fetch members
  watch(() => teams.current, async (current) => {
    if (current?.id) {
      await get()
    } else {
      data.value = []
      meta.value = {}
      error.value = null
    }
  })

  const authMember = computed(() => data.value.find(member => member.email === user.data.email))

  async function get (page = null, inline = false) {
    if (teams.current?.id) {
      try {
        loading.value = true
        error.value = null

        const response = await $api.get(`/teams/${teams.current.id}/members`, {
          page: page || meta.value.current_page || 1
        })

        if (inline) {
          return response
        }

        data.value = response.data
        meta.value = response.meta
      } catch (err) {
        console.error('Failed to fetch team members:', err)
        error.value = getErrorMessage(err)
      } finally {
        loading.value = false
      }
    }
  }

  async function archive (teamId, params) {
    try {
      const response = await $api.delete(`/teams/${teamId}/members`, params)
      // Manually refresh data since team doesn't change, only members do
      await get()
      return response
    } catch (err) {
      console.error('Failed to archive team member:', err)
      error.value = getErrorMessage(err, 'Failed to archive team member')
      throw err
    }
  }

  async function restore (teamId, params) {
    try {
      const response = await $api.post(`/teams/${teamId}/members/restore`, params)
      // Manually refresh data since team doesn't change, only members do
      await get()
      return response
    } catch (err) {
      console.error('Failed to restore team member:', err)
      error.value = getErrorMessage(err, 'Failed to restore team member')
      throw err
    }
  }

  async function updateRole (teamId, memberId, role) {
    try {
      loading.value = true
      error.value = null

      const response = await $api.patch(`/teams/${teamId}/members/role`, {
        id: memberId,
        role
      })

      // Refresh member list to reflect changes
      await get()
      return response
    } catch (err) {
      console.error('Failed to update member role:', err)
      error.value = getErrorMessage(err, 'Failed to update member role')
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    data,
    meta,
    loading,
    error,
    authMember,
    get,
    archive,
    restore,
    updateRole
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTeamMembers, import.meta.hot))
}
